import { useContext, useEffect, useLayoutEffect } from 'react';
import * as yup from 'yup';
import _ from 'lodash';
import { styled, useTheme } from '@mui/material/styles';
import history from '@history';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { handleLogin } from 'app/store/auth/asyncThunk';
import { Controller, useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Card, CircularProgress } from '@mui/material';
import { Link } from 'react-router-dom';
import AvatarGroup from '@mui/material/AvatarGroup';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { regexEmail } from 'src/app/utils/validations';
import { requestLogin } from 'src/app/api/auth';
import { BootstrapContext } from 'src/app/bootstrap/BootstrapContext';

const TOKEN_TYPE_KEY = 'TwoFactorToken';

const IconBadge = styled('div')(({ theme }) => ({
  className: 'badge flex items-start py-4 px-8 mx-8 rounded',
  '& .login-icon': {
    height: '6rem',
    width: 'auto',
  },
}));

const schema = yup.object().shape({
  email: yup
    .string()
    .required('El campo correo no puede quedar vacío')
    .matches(regexEmail, 'Solo letras y espacios entre nombres'),
  password: yup
    .string()
    .required('Ingrese su contraseña')
    .min(6, 'Contraseña muy corta - debe contener al menos 4 caracteres.'),
});

const defaultValues = {
  email: '',
  password: '',
  remember: false,
};

function SignInPage() {
  const { initializeSession, finalizeSession, forceKillSession } = useContext(BootstrapContext);

  const _theme = useTheme();

  const { errorMessages, isCheckingAuth } = useSelector((state) => state.auth);

  const Dispatch = useDispatch();

  const { control, formState, handleSubmit, setValue } = useForm({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(schema),
  });

  const onSubmit = async ({ email, password }) => {
    try {
      const response = await Dispatch(
        handleLogin({ _request: requestLogin, props: [{ email, password }] })
      ).unwrap();
      console.log(response);
      const { tokenType, tokenValidate, redirectAuth, message } = response;
      if (
        typeof tokenType === 'string' &&
        tokenType === TOKEN_TYPE_KEY &&
        typeof tokenValidate === 'string'
      ) {
        history.push(redirectAuth, {
          tokenValidate,
          message,
        });
        return;
      }
      const result = await initializeSession();
      console.log('initialize session result =>', result);
    } catch (err) {
      console.error(err);
      finalizeSession(true);
    }
  };

  const { isValid, dirtyFields, errors } = formState;

  useEffect(() => {
    setValue('email', '', { shouldDirty: true, shouldValidate: true });
    setValue('password', '', { shouldDirty: true, shouldValidate: true });
  }, []);

  useLayoutEffect(() => {
    forceKillSession();
  }, []);

  return (
    <div className="flex flex-col sm:flex-row items-center md:items-start sm:justify-center md:justify-start flex-1 min-w-0">
      <Paper className="h-full sm:h-auto md:flex md:items-center md:justify-end w-full sm:w-auto md:h-full md:w-1/2 py-8 px-16 sm:p-48 md:p-64 sm:rounded-2xl md:rounded-none sm:shadow md:shadow-none ltr:border-r-1 rtl:border-l-1">
        <div className="w-full max-w-320 sm:w-320 mx-auto sm:mx-0">
          <Typography className="mt-32 text-4xl font-extrabold tracking-tight leading-tight">
            Iniciar sesión
          </Typography>
          <Typography variant="subtitle2" className="my-20" gutterBottom>
            ¿No tienes una cuenta? <Link to="/">Contactanos</Link>
          </Typography>

          <Card sx={{ backgroundColor: '#e2f3fb', boxShadow: 'none' }}>
            <div className="flex justify-center items-center p-12">
              <Typography className="text-sm text-start text-wrap" color="primary">
                Inicia sesión con tu correo de administrador
                <span className="font-semibold">&nbsp; Biometrics</span>.
              </Typography>
            </div>
          </Card>

          <form name="loginForm" className="flex flex-col justify-center w-full mt-32">
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  className="mb-24"
                  label="Correo electronico"
                  type="email"
                  autoFocus
                  error={!!errors.email || (errorMessages && errorMessages.length > 0)}
                  helperText={errors?.email?.message}
                  variant="outlined"
                  required
                  fullWidth
                  autoComplete="email"
                  placeholder="trabajador@empresa.com"
                />
              )}
            />

            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  className="mb-24"
                  label="Contraseña"
                  type="password"
                  error={!!errors.password || (errorMessages && errorMessages.length > 0)}
                  helperText={errors?.password?.message || errorMessages.join()}
                  variant="outlined"
                  required
                  fullWidth
                />
              )}
            />

            <div className="flex flex-col sm:flex-row items-center justify-center">
              <Link
                className="text-md font-medium"
                to="/sign-recovery"
                style={{ color: _theme.palette.primary.main }}
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <Button
              variant="contained"
              color="secondary"
              className=" w-full mt-24"
              aria-label="Iniciar sesión"
              disabled={_.isEmpty(dirtyFields) || !isValid}
              fullWidth
              onClick={handleSubmit(onSubmit)}
            >
              {isCheckingAuth ? <CircularProgress size="1.8rem" /> : 'Entrar'}
            </Button>
          </form>
        </div>
      </Paper>

      <Box
        className="relative hidden md:flex flex-auto items-center justify-center h-full p-64 lg:px-112 overflow-hidden"
        sx={{ backgroundColor: 'primary.main' }}
      >
        <svg
          className="absolute inset-0 pointer-events-none"
          viewBox="0 0 960 540"
          width="100%"
          height="100%"
          preserveAspectRatio="xMidYMax slice"
          xmlns="http://www.w3.org/2000/svg"
        >
          <Box
            component="g"
            sx={{ color: 'primary.light' }}
            className="opacity-20"
            fill="none"
            stroke="currentColor"
            strokeWidth="100"
          >
            <circle r="234" cx="196" cy="23" />
            <circle r="234" cx="790" cy="491" />
          </Box>
        </svg>
        <Box
          component="svg"
          className="absolute -top-64 -right-64 opacity-20"
          sx={{ color: 'primary.light' }}
          viewBox="0 0 220 192"
          width="220px"
          height="192px"
          fill="none"
        >
          <defs>
            <pattern
              id="837c3e70-6c3a-44e6-8854-cc48c737b659"
              x="0"
              y="0"
              width="20"
              height="20"
              patternUnits="userSpaceOnUse"
            >
              <rect x="0" y="0" width="4" height="4" fill="currentColor" />
            </pattern>
          </defs>
          <rect width="220" height="192" fill="url(#837c3e70-6c3a-44e6-8854-cc48c737b659)" />
        </Box>

        <div className="z-10 relative w-full max-w-2xl">
          <div className="flex space-x-16 w-full items-center flex-row">
            <IconBadge>
              <img
                src="/assets/images/brands/brand-extended-white-001.svg"
                alt="logo de biometrics"
              />
            </IconBadge>
          </div>
          <div className="mt-24 text-lg tracking-tight leading-6 text-gray-400">
            Wd helps developers to build organized and well coded dashboards full of beautiful and
            rich modules. Join us and start building your application today.
          </div>
          <div className="flex items-center mt-32">
            <AvatarGroup
              sx={{
                '& .MuiAvatar-root': {
                  borderColor: 'primary.main',
                },
              }}
            >
              <Avatar src="assets/images/avatars/female-18.jpg" />
              <Avatar src="assets/images/avatars/female-11.jpg" />
              <Avatar src="assets/images/avatars/male-09.jpg" />
              <Avatar src="assets/images/avatars/male-16.jpg" />
            </AvatarGroup>

            <div className="ml-16 font-medium tracking-tight text-gray-400">
              More than 17k people joined us, it's your turn
            </div>
          </div>
        </div>
      </Box>
    </div>
  );
}

export default SignInPage;
